import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ContentActions from "../components/content/content-actions";
import StructuredArticleRenderer from "../components/content/StructuredArticleRenderer";
import Card from "../components/shared/card";
import MarkdownRenderer from "../components/shared/markdown/render-md";
import ThemedSpinner from "../components/shared/spinner";
import Typography from "../components/shared/typography";
import { ContentActionsWrapper } from "../hooks/ContentActionsContext";
import { usePageContext } from "../hooks/PageContext";
import useLoadContentByIdOrSlug from "../hooks/useLoadContentById";
import { useSimilarContent } from "../hooks/useSimilarContent";

const ContentPageChild = () => {
  const { contentId } = useParams();

  const { content, isLoading, error } = useLoadContentByIdOrSlug(
    contentId || ""
  );

  const { setContent: setPageContent } = usePageContext();
  const { relatedContent, isLoading: isLoadingSimilarContent } = useSimilarContent(
    content?.id,
    { enabled: !!content }
  );

  const [audioUrl, setAudioUrl] = useState<string>("");
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (content) {
      if (content?.audio_url) {
        setAudioUrl(content.audio_url);
      }

      setPageContent(content.text || "");
    }

    return () => {
      setPageContent("");
    };
  }, [content]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <ThemedSpinner size="lg" />
      </div>
    );
  }

  if (!content || error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Typography variant="h3" className="text-terracotta">
          Content not found
        </Typography>
      </div>
    );
  }

  // Add extra top padding for specific content slugs
  const extraTopPadding =
    content?.slug === "ai-medical-second-opinions" ? "pt-16" : "pt-8";

  const filteredSimilarContent =
    relatedContent?.filter((item) => item.id !== content.id) ?? [];

  return (
    <div
      className={`container mx-auto px-4 ${extraTopPadding} pb-8 flex flex-col gap-4 relative`}
    >
      <ContentActions content={content} />

      {audioUrl && (
        <div className="flex flex-col gap-4 items-center">
          <Typography className="mb-6 text-primary">
            Audio Discussion
          </Typography>
          <audio ref={audioRef} preload="auto" src={audioUrl} controls />
        </div>
      )}

      {Array.isArray(content.blocks) && content.blocks.length > 0 ? (
        <StructuredArticleRenderer
          sections={Array.isArray(content.sections) ? content.sections : []}
          blocks={content.blocks}
          assets={content.assets || []}
        />
      ) : (
        <MarkdownRenderer content={content.text} />
      )}

      {isLoadingSimilarContent ? (
        <div className="space-y-2">
          <Typography variant="h4" className="text-terracotta">
            Related Articles
          </Typography>
          <Typography variant="body2" className="text-brown-700">
            Loading related articles…
          </Typography>
        </div>
      ) : filteredSimilarContent.length > 0 ? (
        <section className="space-y-3">
          <Typography variant="h4" className="text-terracotta">
            Related Articles
          </Typography>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filteredSimilarContent.map((similarContent) => {
              const title =
                similarContent.title || similarContent.slug || "Untitled";
              const snippet =
                similarContent.snippet ||
                "No preview available for this related article.";

              return (
                <Link
                  key={similarContent.id}
                  to={`/content/${similarContent.slug || similarContent.id}`}
                  className="block"
                >
                  <Card
                    className="border border-beige-200 hover:border-terracotta-300"
                    variant="cream"
                    elevation={1}
                    hoverEffect
                  >
                    <Card.Body className="space-y-2">
                      <Typography variant="h6" className="text-brown-900">
                        {title}
                      </Typography>
                      <Typography variant="body2" className="text-brown-700">
                        {snippet}
                      </Typography>
                    </Card.Body>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
};

const ContentPage = () => {
  return (
    <ContentActionsWrapper>
      <ContentPageChild />
    </ContentActionsWrapper>
  );
};

export default ContentPage;
